import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ops from "../../styles/opsConsole.module.css";
import css from "./Vendors.module.css";
import { Kpi, Tile, IcoStore, IcoCheck, IcoShield, IcoWarn } from "../../styles/opsBits";
import {
  ErrorCard,
  Search,
  SkeletonRows,
  SortTh,
  StatusIcon,
  Th,
  VerificationBadge,
} from "./VendorBits";
import {
  STATUS_KEYS,
  STATUS_LABEL,
  VENDOR_SORTS,
  ago,
  date,
  getPage,
  pct,
  shapeVendor,
  sortRows,
} from "./vendorApi";

/**
 * THE VENDOR DIRECTORY
 * ====================
 * The supplier base, enumerable at last. Before this screen the only route to
 * a vendor was through one of their pending submissions, so a vendor with an
 * empty queue was unreachable and "who is still unverified" had no answer.
 *
 * Three things an operator does here, in order of how often they do them:
 * find one vendor, see who is blocked, and open a record. Everything on the
 * screen serves one of those; anything that served none of them is gone.
 */

/* Sorting is SERVER-SIDE and authoritative — ?sort= and ?dir= go out on
   every request. The same keys are re-applied client-side afterwards purely
   so the arrow in the header cannot lie while the server side of this
   contract is still landing. See sortRows() for why. */
const DEFAULT_SORT = "joined";
const DEFAULT_DIR = "desc";

/* Which direction a column should open in. Nobody wants "oldest first" or
   the emptiest vendor at the top on the first click. */
const FIRST_DIR = {
  name: "asc",
  gstin: "asc",
  status: "asc",
  listings: "desc",
  joined: "desc",
  last_activity: "desc",
};

export default function VendorList() {
  const navigate = useNavigate();

  /* Filters live in the URL so a filtered view is a link an operator can
     bookmark or paste to a colleague — the state is about the work, not
     about this tab. */
  /* Every value read out of the URL is validated here and nowhere else. A
     hand-edited, stale or bookmarked link is untrusted input: an unknown
     ?status= used to leave no chip selected and print the literal word
     "undefined" in the empty state, and an unknown ?sort= would be handed
     straight to an API that answers unknown sort keys with a 400. Anything
     unrecognised silently becomes the default. */
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const rawStatus = params.get("status");
  const status = rawStatus === "all" || STATUS_KEYS.includes(rawStatus) ? rawStatus : "all";
  const sort = VENDOR_SORTS[params.get("sort")] ? params.get("sort") : DEFAULT_SORT;
  const dir = params.get("dir") === "asc" ? "asc" : params.get("dir") === "desc" ? "desc" : DEFAULT_DIR;

  const [draft, setDraft] = useState(q);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const patch = useCallback(
    (next) => {
      const merged = { q, status, sort, dir, ...next };
      const out = {};
      Object.entries(merged).forEach(([k, v]) => {
        // Keep the URL readable: only carry what differs from the default.
        const isDefault =
          (k === "q" && !v) ||
          (k === "status" && v === "all") ||
          (k === "sort" && v === DEFAULT_SORT) ||
          (k === "dir" && v === DEFAULT_DIR);
        if (!isDefault) out[k] = v;
      });
      setParams(out, { replace: true });
    },
    [q, status, sort, dir, setParams]
  );

  /* Typing should not fire a request per keystroke. */
  useEffect(() => setDraft(q), [q]);
  useEffect(() => {
    if (draft === q) return undefined;
    const t = setTimeout(() => patch({ q: draft }), 280);
    return () => clearTimeout(t);
  }, [draft, q, patch]);

  /**
   * One request, deliberately unfiltered by status.
   *
   * The chips have to show a live count for EVERY state at once, which a
   * status-filtered response cannot provide — and with the whole supplier
   * base in hand, switching chips is instant rather than a round trip. So
   * status=all goes to the server and the chip is applied to the rows we
   * already hold. Search and sort do go to the server.
   *
   * `sort` and `dir` are validated against VENDOR_SORTS before they leave
   * this component, so a hand-edited or stale URL can never put the table in
   * a state the API answers with 400 — a bad ?sort= silently falls back to
   * the default rather than wedging the screen. If a 400 does arrive anyway
   * the API's own message ("sort must be one of …") is what gets shown,
   * alongside a control that clears the view.
   */
  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    setError(null);
    // limit is explicit and near the server's ceiling: the chip counts are
    // computed from this response, so a truncated one would under-count.
    const qs = new URLSearchParams({ q, status: "all", sort, dir, limit: "500" });
    getPage(`/admin/vendors?${qs}`, ctl.signal)
      .then(({ data, meta: m }) => {
        setRows((Array.isArray(data) ? data : []).map(shapeVendor));
        setMeta(m);
        setLoading(false);
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setRows([]);
        setMeta(null);
        setError(e.message || "Could not load vendors.");
        setLoading(false);
      });
    return () => ctl.abort();
  }, [q, sort, dir, reloadKey]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    STATUS_KEYS.forEach((k) => {
      c[k] = 0;
    });
    rows.forEach((r) => {
      c[r.verification_status] = (c[r.verification_status] || 0) + 1;
    });
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (status !== "all" && r.verification_status !== status) return false;
      // Client-side search is a safety net only; the server already did it.
      if (!needle) return true;
      return [r.name, r.company, r.owner_email, r.gstin]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle));
    });
    return sortRows(filtered, VENDOR_SORTS, sort, dir);
  }, [rows, q, status, sort, dir]);

  const onSort = (key) =>
    patch({ sort: key, dir: sort === key ? (dir === "asc" ? "desc" : "asc") : FIRST_DIR[key] });

  const open = (id) => navigate(`/dashboard/vendors/${id}`);

  const blocked = rows.filter((r) => r.verification_status !== "verified").length;
  const noMargin = rows.filter((r) => r.margin_rate === null).length;
  const openReviews = rows.reduce((s, r) => s + r.open_reviews, 0);

  const sortProps = { sort, dir, onSort };

  return (
    <div className={`${ops.root} ${css.page}`}>
      <div className={css.headTight}>
        <div>
          <h1>Vendors</h1>
          <p className={css.headSub}>
            Everyone selling through Easexpense. Verification is a gate, not a
            label — an unverified vendor's listings cannot be published.
          </p>
        </div>
        <div className={css.search}>
          <Search />
          <input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search name, company, email, GSTIN"
            aria-label="Search vendors"
          />
          {draft && (
            <button
              type="button"
              className={css.searchClear}
              onClick={() => setDraft("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className={`${ops.kpis} ${css.kpiStrip}`}>
        <Kpi value={rows.length} label="Vendors" tone="toneInfo" icon={IcoStore} />
        <Kpi
          value={counts.verified || 0}
          label="Verified — can publish"
          tone="toneOk"
          icon={IcoCheck}
        />
        <Kpi
          value={blocked}
          label="Blocked from publishing"
          tone="toneWarn"
          icon={IcoShield}
        />
        <Kpi
          value={noMargin}
          label="No margin agreed"
          tone="toneDanger"
          icon={IcoWarn}
        />
      </div>

      {/* Chips are a radio group: exactly one is on, and the browser's own
          arrow-key roving works because each is a real button in a group. */}
      <div className={`${ops.filters} ${css.chipRow}`} role="group" aria-label="Filter by verification status">
        {[["all", "All"], ...STATUS_KEYS.map((k) => [k, STATUS_LABEL[k]])].map(
          ([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={status === key}
              onClick={() => patch({ status: key })}
              className={`${ops.chip} ${status === key ? ops.chipOn : ""}`}
            >
              {key !== "all" && <StatusIcon status={key} />}
              {label}
              <span className={ops.chipCount}>{counts[key] || 0}</span>
            </button>
          )
        )}
        {openReviews > 0 && (
          <span className={css.subtle} style={{ fontSize: 12, marginLeft: 4 }}>
            {openReviews} submission{openReviews === 1 ? "" : "s"} waiting on review
          </span>
        )}
      </div>

      {error ? (
        <>
          <ErrorCard message={error} onRetry={() => setReloadKey((k) => k + 1)} />
          {params.toString() && (
            <button type="button" className={`${ops.btn} ${css.btnPlain}`} onClick={() => setParams({}, { replace: true })}>
              Clear search, filter and sort
            </button>
          )}
        </>
      ) : (
        <div className={ops.card}>
          <div className={css.tableWrap}>
            <table className={ops.table}>
              <thead>
                <tr>
                  <SortTh id="name" label="Vendor" {...sortProps} />
                  <SortTh id="gstin" label="GSTIN" {...sortProps} />
                  <SortTh id="status" label="Verification" {...sortProps} />
                  <Th label="Margin" numeric />
                  <SortTh id="listings" label="Listings" numeric {...sortProps} />
                  <Th label="In review" numeric />
                  <SortTh id="joined" label="Joined" {...sortProps} />
                  <SortTh id="last_activity" label="Last activity" {...sortProps} />
                </tr>
              </thead>
              <tbody>
                {loading && <SkeletonRows cols={8} />}

                {!loading && shown.length === 0 && (
                  <tr>
                    <td colSpan={8} className={ops.empty}>
                      {/* Say WHY it is empty. A search that found nothing is a
                          different message from a supplier base that is
                          genuinely empty, and the server having already
                          filtered by q means rows.length can be 0 in both
                          cases — so the search term is checked first. */}
                      {q
                        ? `Nothing matches “${q}”${status === "all" ? "" : ` in ${STATUS_LABEL[status]}`}.`
                        : status !== "all"
                          ? `No vendors are ${STATUS_LABEL[status].toLowerCase()}.`
                          : "No vendors yet."}
                    </td>
                  </tr>
                )}

                {!loading &&
                  shown.map((v) => (
                    <tr
                      key={v.id}
                      className={`${ops.row} ${css.linkRow} ${
                        v.verification_status === "verified"
                          ? ops.rowOk
                          : v.verification_status === "rejected"
                            ? ops.rowDanger
                            : ops.rowWarn
                      }`}
                      onClick={() => open(v.id)}
                    >
                      <td>
                        <span className={ops.idCell}>
                          <Tile name={v.name} />
                          <span className={css.stack}>
                            {/* A real button, so the row is reachable and
                                openable from the keyboard as well. */}
                            <button
                              type="button"
                              className={css.cellBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                open(v.id);
                              }}
                            >
                              {v.name || "Unnamed vendor"}
                            </button>
                            <small>{v.owner_email || "no owner email"}</small>
                          </span>
                        </span>
                      </td>
                      <td className={ops.mono}>
                        {v.gstin || <span className={css.gapVal}>none</span>}
                      </td>
                      <td>
                        <VerificationBadge status={v.verification_status} small />
                      </td>
                      {/* A NULL rate is not 0% — nobody has agreed one. The
                          two must never look the same on a supplier list. */}
                      <td className={ops.num}>
                        {v.margin_rate === null ? (
                          <span className={ops.warnText}>not set</span>
                        ) : (
                          pct(v.margin_rate)
                        )}
                      </td>
                      <td className={ops.num}>
                        <span className={css.stack}>
                          <span className={css.tnum}>{v.listings_count}</span>
                          {v.listings_count > 0 && (
                            <small className={css.tnum}>
                              {v.listings_live} live · {v.listings_draft} draft
                            </small>
                          )}
                        </span>
                      </td>
                      <td className={ops.num}>
                        {v.open_reviews > 0 ? (
                          <span className={`${ops.badge} ${ops.warn}`}>
                            {v.open_reviews}
                          </span>
                        ) : (
                          <span className={css.subtle}>—</span>
                        )}
                      </td>
                      <td className={ops.nowrap}>{date(v.created_at) || "—"}</td>
                      <td className={ops.nowrap}>
                        {v.last_activity ? (
                          <span className={css.stack}>
                            <span>{ago(v.last_activity)}</span>
                            <small>{date(v.last_activity)}</small>
                          </span>
                        ) : (
                          <span className={css.gapVal}>no activity</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {/* The chip counts are computed from what came back, so say so if
              the server capped the page rather than quietly under-counting. */}
          {meta && Number(meta.count) > rows.length && (
            <p className={ops.note}>
              Showing the first {rows.length} of {meta.count} vendors — the counts above
              cover this page only.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
